import CampusSelect from "components/CampusSelect"
import {KlassappTableHOC} from "HOC"
import {useCallback, useEffect, useState} from "react"
import {useDebounce} from "react-use"
import {majorVersionsService} from "services"
import {KlassappTableProps} from "types/common"
import {BaseNewFilter, KlassappTable, KlassappTableHeader} from "uiKit"
import {Major} from "types/major"
import {useQuery} from "@tanstack/react-query"
import {Col, Row} from "antd"
import {Campus} from "types/campus"

const INITIAL_FILTER = {
    campuses: [] as Campus.Campus[]
}

type ProgramTableProps = KlassappTableProps & {
    setSelectedProgram: (program: Major.MajorVersion) => void
}

const ProgramTable = (props: ProgramTableProps) => {
    const {
        dispatch,
        dispatchFunc,
        setSelectedProgram,
        page,
        pageSize,
        allFields,
        fields,
        tableHeaderActions,
        orderField,
        columns,
        isShowTableHeaderAction
    } = props
    const [search, setSearch] = useState("")
    const [searchDebounce, setSearchDebounce] = useState("")
    const [filter, setFilter] = useState(INITIAL_FILTER)
    const [appliedFilter, setAppliedFilter] = useState(INITIAL_FILTER)
    useDebounce(() => setSearchDebounce(search), 500, [search])

    const campusIds = appliedFilter.campuses?.length ? appliedFilter.campuses.map((campus) => campus.id) : undefined

    const {data: programVersions, isFetching} = useQuery(
        ["list-program-versions", page, pageSize, searchDebounce, orderField, campusIds],
        async () => {
            const response = await majorVersionsService.getAll({
                range: {page, pageSize: pageSize},
                linkedEntities: true,
                filter: {
                    search: searchDebounce,
                    type: Major.Unit.Clock,
                    campusIds
                },
                sort: {orderBy: orderField.field ?? "id", orderDir: orderField.order ?? "DESC"}
            })
            if (response.data) {
                response.data = response.data.map((item) => {
                    return {
                        ...item,
                        programName: item.program.name,
                        programCode: item.program.code
                    }
                })
            }
            return response
        },
        {
            initialData: {
                data: [],
                total: 0
            }
        }
    )

    const getFields = () => {
        return ["Program Name", "Program Code", "Program Version Code", "Program Version Name", "Campuses"]
    }

    const getPageTitle = () => {
        return "Major"
    }

    const getColumns = () => {
        return [
            {
                title: "Program Name",
                field: "programName"
            },
            {
                title: "Program Code",
                field: "programCode"
            },
            {
                title: "Program Version Code",
                field: "version_code",
                orderField: "versionCode",
                sortable: true
            },
            {
                title: "Program Version Name",
                field: "version_name",
                orderField: "versionName",
                sortable: true
            },
            {
                title: "Campuses",
                field: "campuses",
                render: (campuses) => campuses.map((campus) => campus.name).join(", ")
            }
        ]
    }

    useEffect(() => {
        dispatch({isClassComponent: false})
        dispatchFunc([
            {key: "getPageTitle", func: getPageTitle},
            {key: "getFields", func: getFields},
            {key: "getColumns", func: getColumns}
        ])
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [programVersions])

    const onClickRowItem = (row) => {
        setSelectedProgram(row)
    }

    const onChangeFilter = useCallback((key: keyof typeof filter, value: Campus.Campus[]) => {
        setFilter((prev) => ({...prev, [key]: value}))
    }, [])

    const onClearFilter = useCallback(() => {
        setFilter(INITIAL_FILTER)
        setAppliedFilter(INITIAL_FILTER)
    }, [])

    const onApplyFilter = useCallback(() => {
        setAppliedFilter(filter)
    }, [filter])

    const onSearchInput = useCallback((value = "") => {
        setSearch(value)
    }, [])

    return (
        <>
            <BaseNewFilter
                filter={filter}
                searchValue={search}
                onSearchInput={onSearchInput}
                onClick={onApplyFilter}
                onClickClear={onClearFilter}>
                <Row gutter={[40, 32]}>
                    <Col span={12}>
                        <CampusSelect
                            isMulti
                            isClearable
                            placeholder="Campus"
                            value={filter.campuses}
                            onChange={(newValue) => onChangeFilter("campuses", newValue ?? [])}
                        />
                    </Col>
                </Row>
            </BaseNewFilter>
            <KlassappTableHeader
                isShowAction={isShowTableHeaderAction}
                actions={tableHeaderActions}
                page={page}
                total={programVersions.total}
                defaultPageSize={pageSize}
                onChangePage={props.onChangePage}
                onChangeRowPerPage={props.onChangeRowPerPage}
            />
            <KlassappTable
                columns={columns}
                fields={fields}
                data={programVersions.data}
                menuActions={[]}
                isLoading={isFetching}
                allFields={allFields}
                isShowCheckedColumn={false}
                orderField={orderField}
                onClickRowItem={onClickRowItem}
                onChangeFields={props.onChangeFields}
                onChangeAllFields={props.onChangeAllFields}
                onUpdateRowData={props.onUpdateRowData}
                onClickSortColumn={props.onClickSortColumn}
                onUpdateTableData={props.onUpdateTableData}
                onDraggableColumn={props.onDraggableColumn}
            />
        </>
    )
}

export default KlassappTableHOC(ProgramTable)
